export const formatLocalDateTime = (date, options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
}) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // Use the user's locale; fall back to en-US.
    const locale = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    // Get formatted date parts
    const formatter = new Intl.DateTimeFormat(locale, options);
    const parts = formatter.formatToParts(date);
    // Extract parts
    const weekday = ((_a = parts.find((p) => p.type === 'weekday')) === null || _a === void 0 ? void 0 : _a.value) || '';
    const month = ((_b = parts.find((p) => p.type === 'month')) === null || _b === void 0 ? void 0 : _b.value) || '';
    const day = ((_c = parts.find((p) => p.type === 'day')) === null || _c === void 0 ? void 0 : _c.value) || '';
    const year = ((_d = parts.find((p) => p.type === 'year')) === null || _d === void 0 ? void 0 : _d.value) || '';
    const hour = ((_e = parts.find((p) => p.type === 'hour')) === null || _e === void 0 ? void 0 : _e.value) || '';
    const minute = ((_f = parts.find((p) => p.type === 'minute')) === null || _f === void 0 ? void 0 : _f.value) || '';
    const dayPeriod = ((_h = (_g = parts.find((p) => p.type === 'dayPeriod')) === null || _g === void 0 ? void 0 : _g.value) === null || _h === void 0 ? void 0 : _h.toLowerCase()) || '';
    // Get timezone abbreviation
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Format: "Tuesday - September 9, 2025 - 3:00 pm PST"
    return `${weekday} - ${month} ${day}, ${year} - ${hour}:${minute} ${dayPeriod} ${timeZone}`;
};
/**
 * Formats an ISO-8601 date string (YYYY-MM-DD) into "DD Mon YYYY"
 * (e.g. "20 Nov 2020"). Parses date parts directly to avoid timezone drift.
 * Returns an empty string for missing or invalid input.
 */
export const formatHistoryDate = (isoDate) => {
    if (!isoDate)
        return '';
    const parts = isoDate.split('-');
    if (parts.length !== 3)
        return '';
    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
        return '';
    }
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return '';
    }
    const monthName = date.toLocaleString('en-US', { month: 'short' });
    return `${day} ${monthName} ${year}`;
};
const getOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13)
        return 'th';
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};
/**
 * Takes a date of birth string (e.g. "1994-04-10") and returns a formatted
 * string like "10th Apr, 1994 (32 years)".
 * Falls back to "Unknown" for invalid or partial dates.
 */
export const formatDobWithAge = (dob) => {
    if (!dob)
        return 'Unknown';
    const parts = dob.split('-');
    // Ensure full date (YYYY-MM-DD)
    if (parts.length !== 3)
        return 'Unknown';
    const [year, month, day] = parts.map(Number);
    // Validate numbers
    if (isNaN(year) || isNaN(month) || isNaN(day) || month < 1 || month > 12 || day < 1 || day > 31) {
        return 'Unknown';
    }
    const birthDate = new Date(year, month - 1, day);
    // Extra validation (handles invalid dates like Feb 30)
    if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
        return 'Unknown';
    }
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
    const monthName = birthDate.toLocaleString('en-US', { month: 'short' });
    const today = new Date();
    let age = today.getFullYear() - year;
    const hasBirthdayPassed = today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
    if (!hasBirthdayPassed)
        age--;
    return `${dayWithSuffix} ${monthName}, ${year} (${age} years)`;
};
